/**
 * Merge Tool Render Module (ISS-04-139)
 * Pure DOM-string builders — no state, no event binding.
 */

import type { MergeEntity } from "./merge-tool-api";

type UnknownRecord = Record<string, unknown>;

export interface MergeFieldConfig {
    idKey: string;
    idLabel: string;
    nameKey: string;
    imageKey: string | null;
    // Read-only counts of data attached to this entity (posts, reviews, ...)
    // — not merge-selectable fields, just context to help judge which side
    // actually has real data behind it before picking a merge target.
    childCountFields: Array<{ key: string; label: string }>;
    fields: Array<{ key: string; label: string }>;
}

export const MERGE_FIELD_CONFIG: Record<MergeEntity, MergeFieldConfig> = {
    brands: {
        idKey: "brand_id",
        idLabel: "Brand ID",
        nameKey: "brand_name",
        imageKey: "image_url",
        childCountFields: [
            { key: "products_count", label: "Products" },
            { key: "review_count", label: "Reviews" },
            { key: "posts_count", label: "Posts" },
        ],
        fields: [
            { key: "brand_name", label: "Brand name" },
            { key: "image_url", label: "Image" },
            { key: "brand_description_th", label: "Description (TH)" },
            { key: "brand_description_en", label: "Description (EN)" },
            { key: "brand_description_ja", label: "Description (JA)" },
            { key: "activate_time", label: "Activate time" },
            { key: "priority", label: "Priority" },
            { key: "country", label: "Country" },
            { key: "website", label: "Website" },
            { key: "facebook", label: "Facebook" },
            { key: "instagram", label: "Instagram" },
            { key: "youtube", label: "YouTube" },
            { key: "tiktok", label: "TikTok" },
            { key: "x", label: "X" },
        ],
    },
    products: {
        idKey: "product_id",
        idLabel: "Product ID",
        nameKey: "product_name",
        imageKey: "image",
        childCountFields: [
            { key: "review_count", label: "Reviews" },
            { key: "posts_count", label: "Posts" },
            { key: "likes_count", label: "Likes" },
            { key: "comments_count", label: "Comments" },
            { key: "reads_count", label: "Reads" },
        ],
        fields: [
            { key: "product_name", label: "Product name" },
            { key: "image", label: "Image" },
            { key: "product_url", label: "Product URL" },
            { key: "price", label: "Price" },
            { key: "price_unit", label: "Price unit" },
            { key: "category_id", label: "Category ID" },
            { key: "brand_id", label: "Brand ID" },
            { key: "group_id", label: "Group ID" },
            { key: "unit", label: "Unit" },
            { key: "how_to_use", label: "How to use" },
            { key: "ingredients", label: "Ingredients" },
            { key: "general_description", label: "Description" },
            { key: "keyword", label: "Keyword" },
            { key: "manual_score_offset", label: "Manual score offset" },
        ],
    },
};

function escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
}

function renderFieldValue(value: unknown): string {
    if (value === null || value === undefined || value === "") {
        return '<span class="merge-tool-field__value--empty">(empty)</span>';
    }
    if (typeof value === "object") {
        return escapeHtml(JSON.stringify(value));
    }
    return escapeHtml(String(value));
}

// The image field is chosen the same way any other field is (click one
// side), but also shows a thumbnail so the choice can be made by sight
// rather than only by reading the URL/filename.
function renderImageFieldValue(value: unknown): string {
    if (typeof value !== "string" || value === "") {
        return '<span class="merge-tool-field__value--empty">(empty)</span>';
    }

    return `<img class="merge-tool-field__thumb" src="${escapeHtml(value)}" alt="" referrerpolicy="no-referrer"><span class="merge-tool-field__thumb-label">${escapeHtml(value)}</span>`;
}

function renderChildCounts(item: UnknownRecord, config: MergeFieldConfig): string {
    const chips = config.childCountFields
        .map((field) => {
            const raw = item[field.key];
            const count = typeof raw === "number" ? raw : Number(raw ?? 0);
            return `<span class="merge-tool-card__count-chip">${escapeHtml(field.label)}: ${Number.isFinite(count) ? count : 0}</span>`;
        })
        .join("");

    return `<div class="merge-tool-card__counts">${chips}</div>`;
}

function renderIdBlock(config: MergeFieldConfig, id: number, editable: boolean): string {
    // Both sides render as the same-sized textbox — a plain text value next
    // to an editable input made the two cards different heights.
    const value = editable
        ? `<input type="number" min="1" class="merge-tool-card__id-input form-control" data-role="merge-tool-manual-target-input" value="${id || ""}" placeholder="${escapeHtml(config.idKey)}">`
        : `<input type="text" readonly class="merge-tool-card__id-input form-control" value="${id}">`;

    return `<div class="merge-tool-card__id-label">${escapeHtml(config.idLabel)}</div>${value}`;
}

// Brands have a dedicated SVG symbol (vlf-brands, the same one the navbar's
// "Brands" link uses — see MenuIconHelper::MAP) already present on the page
// via the navbar's own <x-menu-icon>, so no-image brand cards reuse it
// instead of the generic picture icon. Products have no per-entity icon here
// (unlike the category-SVG treatment product cards get elsewhere), so they
// keep the generic fallback.
function renderImagePlaceholderIcon(entity: MergeEntity): string {
    if (entity === "brands") {
        return '<svg class="merge-tool-card__image-placeholder merge-tool-card__image-placeholder--svg" aria-hidden="true" focusable="false"><use href="#vlf-brands"></use></svg>';
    }

    return '<i class="bi bi-image merge-tool-card__image-placeholder" aria-hidden="true"></i>';
}

// Labels default to Thai text with a data-translate hook, same convention as
// merge_tool_no_candidates below — never hardcode Japanese here.
function renderCardLabel(role: "source" | "target"): string {
    return role === "source"
        ? '<div class="merge-tool-card__label" data-translate="merge_tool_source_label">ต้นทาง</div>'
        : '<div class="merge-tool-card__label" data-translate="merge_tool_target_label">ปลายทาง</div>';
}

function renderCard(item: UnknownRecord, config: MergeFieldConfig, role: "source" | "target", editableId: boolean, entity: MergeEntity): string {
    const id = Number(item[config.idKey] || 0);
    const name = escapeHtml(String(item[config.nameKey] ?? `#${id}`));
    const image = config.imageKey ? item[config.imageKey] : null;
    // Always render the same fixed-height frame, with or without an image,
    // so a source/target pair don't end up different heights.
    const imageInner =
        typeof image === "string" && image !== ""
            ? `<img class="merge-tool-card__image" src="${escapeHtml(image)}" alt="" referrerpolicy="no-referrer">`
            : renderImagePlaceholderIcon(entity);

    return `<div class="merge-tool-card">${renderCardLabel(role)}${renderIdBlock(config, id, editableId)}<div class="merge-tool-card__image-frame">${imageInner}</div><div class="merge-tool-card__name">${name}</div>${renderChildCounts(item, config)}</div>`;
}

// Shown in place of the target card before a target has been resolved — the
// id input doubles as the only way to pick a target when nothing was
// auto-detected from the note (no separate "enter manually" block).
function renderEmptyTargetCard(config: MergeFieldConfig, showHint: boolean): string {
    const hint = showHint
        ? '<p class="merge-tool-card__hint text-muted" data-translate="merge_tool_no_candidates">No merge candidates were auto-detected from this entity\'s note.</p>'
        : "";

    return `<div class="merge-tool-card merge-tool-card--empty">${renderCardLabel("target")}${renderIdBlock(config, 0, true)}${hint}</div>`;
}

function renderCandidateButtons(entity: MergeEntity, candidates: UnknownRecord[], selectedId: number | null): string {
    if (candidates.length <= 1) return "";

    const config = MERGE_FIELD_CONFIG[entity];
    return candidates
        .map((candidate) => {
            const id = Number(candidate[config.idKey] || 0);
            const name = escapeHtml(String(candidate[config.nameKey] ?? `#${id}`));
            return `<button type="button" class="merge-tool-candidate-btn${id === selectedId ? " is-selected" : ""}" data-candidate-id="${id}">${name} (${config.idKey}=${id})</button>`;
        })
        .join("");
}

function renderLoading(): string {
    return '<div class="loading-content"><div class="spinner"></div></div>';
}

function renderColumns(
    entity: MergeEntity,
    source: UnknownRecord,
    target: UnknownRecord | null,
    selections: Record<string, "source" | "target">,
    showNoCandidatesHint: boolean,
): string {
    const config = MERGE_FIELD_CONFIG[entity];
    const sourceCard = renderCard(source, config, "source", false, entity);
    const arrow = '<div class="merge-tool-arrow"><i class="bi bi-arrow-right" aria-hidden="true"></i></div>';

    if (!target) {
        const targetCard = renderEmptyTargetCard(config, showNoCandidatesHint);
        return `<div>${sourceCard}</div>${arrow}<div>${targetCard}</div>`;
    }

    const targetCard = renderCard(target, config, "target", true, entity);

    const rows = config.fields
        .map((field) => {
            const sourceVal = source[field.key];
            const targetVal = target[field.key];
            const selected = selections[field.key] ?? "target";
            const isImageField = field.key === config.imageKey;
            const renderValue = isImageField ? renderImageFieldValue : renderFieldValue;

            return `
            <div class="merge-tool-field-label">${escapeHtml(field.label)}</div>
            <div class="merge-tool-field${isImageField ? " merge-tool-field--image" : ""}">
                <div class="merge-tool-field__value${selected === "source" ? " is-selected" : ""}" data-field="${field.key}" data-side="source">${renderValue(sourceVal)}</div>
                <div class="merge-tool-field__value${selected === "target" ? " is-selected" : ""}" data-field="${field.key}" data-side="target">${renderValue(targetVal)}</div>
            </div>`;
        })
        .join("");

    return `<div>${sourceCard}</div>${arrow}<div>${targetCard}</div><div style="grid-column:1 / -1">${rows}</div>`;
}

export { renderCandidateButtons, renderLoading, renderColumns };
